// =====================================================================================
// INVRT Radar — web console — consent banner designer — choice statistics
//
// GET /sites/:id/consent/stats?days=30: the server's own per-day counts of granted,
// denied and dismissed choices (aggregated in the database, never counted here). The bar
// is proportional to the three totals; the table lists the days that saw a choice. A site
// with no choices yet is told so, with what to do next.
// =====================================================================================

import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import type { ConsentChoiceTotals, ConsentStatsDay } from "@/lib/types.c1";
import { CONSENT_STATS_DAYS, useConsentStats } from "./useConsent";

const DAY_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** "2026-08-20" → "20 August 2026", read as a calendar day rather than a UTC instant. */
function formatDay(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return isoDate;
  return DAY_FORMAT.format(new Date(year, month - 1, day));
}

function formatCount(count: number): string {
  return count.toLocaleString("en-GB");
}

const SEGMENTS: Array<{ key: keyof ConsentChoiceTotals; label: string; className: string }> = [
  { key: "granted", label: "granted", className: "bg-accent" },
  { key: "denied", label: "denied", className: "bg-ink-faint" },
  { key: "dismissed", label: "dismissed", className: "bg-line-strong" },
];

function TotalsBar({ totals }: { totals: ConsentChoiceTotals }) {
  const sum = totals.granted + totals.denied + totals.dismissed;
  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-surface-muted"
        role="img"
        aria-label={`${formatCount(totals.granted)} granted, ${formatCount(totals.denied)} denied, ${formatCount(totals.dismissed)} dismissed`}
      >
        {SEGMENTS.map((segment) => {
          const share = sum === 0 ? 0 : (totals[segment.key] / sum) * 100;
          return share === 0 ? null : (
            <div
              key={segment.key}
              className={segment.className}
              style={{ width: `${share}%` }}
            />
          );
        })}
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-1">
        {SEGMENTS.map((segment) => (
          <li key={segment.key} className="flex items-center gap-2 text-sm text-ink">
            <span className={`h-2.5 w-2.5 rounded-full ${segment.className}`} aria-hidden="true" />
            <span>
              {formatCount(totals[segment.key])} {segment.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DailyTable({ daily }: { daily: ConsentStatsDay[] }) {
  const withChoices = daily
    .filter((day) => day.granted + day.denied + day.dismissed > 0)
    .slice()
    .reverse();
  return (
    <Table>
      <TableCaption>Days without a recorded choice are not listed.</TableCaption>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Day</TableHeaderCell>
          <TableHeaderCell className="text-right">Granted</TableHeaderCell>
          <TableHeaderCell className="text-right">Denied</TableHeaderCell>
          <TableHeaderCell className="text-right">Dismissed</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {withChoices.map((day) => (
          <TableRow key={day.date}>
            <TableCell>{formatDay(day.date)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(day.granted)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(day.denied)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCount(day.dismissed)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ConsentStatsCard({ siteId }: { siteId: string }) {
  const stats = useConsentStats(siteId, CONSENT_STATS_DAYS);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consent choices</CardTitle>
        <CardDescription>
          What visitors chose over the last {CONSENT_STATS_DAYS} days, as recorded by the banner.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {stats.isLoading ? <LoadingState label="Loading consent choices…" /> : null}
        {stats.isError ? (
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        ) : null}
        {stats.data ? (
          stats.data.totals.granted + stats.data.totals.denied + stats.data.totals.dismissed === 0 ? (
            <EmptyState
              title="No choices recorded yet"
              body="Publish the banner and install the snippet to start."
            />
          ) : (
            <>
              <TotalsBar totals={stats.data.totals} />
              <DailyTable daily={stats.data.daily} />
            </>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}
